import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, Image, Label } from 'semantic-ui-react';
import * as yup from 'yup';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DangerText, ShadowBox, SuccessText } from '../elements/Common';
import { StyledButton } from '../elements/Button';
import { PasswordFormField } from '../elements/FormField';
import { ToastMessage } from '../utility/toast';
import { UserService } from '../utility/services/user';
import { Images } from '../images';

const LoginContainer = styled.div`
  min-height: 100vh;
  position: relative;
  z-index: 2;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;

  .verificationContainer {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    margin-bottom: 8px;
  }

  .verificationContainer > div {
    flex: 1;
    padding: 4px;
  }

  .verificationInputContainer {
    display: flex;
    flex: 1;
    min-height: 60px;
    font-weight: bold;
    font-size: 20px;
    line-height: 23px;
    margin-bottom: 1.5em;
  }

  .verificationContainerMobile {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    margin-bottom: 8px;
  }

  .verificationContainerMobile > div {
    flex: 1;
    padding: 1px;
  }

  .verificationContainerMobile {
    display: flex;
    flex: 1;
    min-height: 60px;
    font-weight: bold;
    line-height: 23px;
    margin-bottom: 1.5em;
  }
`;

const PageContainer = styled.div`
  background: ${({ theme }) => theme.colors.box};
`;

const Box = styled(ShadowBox)`
  padding: 3em 2em;
  width: 432px;
  @media only screen and (max-width: 768px) {
    width: calc(100% - 20px);
  }
`;

const LoginText = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  margin-bottom: 1.5em;
`;

const Logo = styled(Image)`
  height: auto;
  width: 220px;
`;

const LogoContainer = styled.a`
  z-index: 9;
  margin-bottom: 50px;
`;

const ResetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password must have at least 12 characters')
    .min(12, 'Password must have at least 12 characters')
    .matches(/[A-Z]/, 'Password should contain at least one upper case letter')
    .matches(/[a-z]/, 'Password should contain at least one lower case letter')
    .matches(/[@$!%*#?&]/, 'Password should contain at least one special character')
    .matches(/[\d]/, 'Password should contain at least one number'),
  confirmPassword: yup
    .string()
    .required()
    .oneOf([yup.ref('password'), null], 'Passwords must match'),
});

function LoginBox({ loading, loginClicked, errors, error, formLoading, control }) {
  return (
    <Box>
      <LoginText>Reset Password</LoginText>
      <Form loading={formLoading} onSubmit={loginClicked}>
        <PasswordFormField
          error={errors.password}
          label="Password"
          name="password"
          control={control}
          required
          hint={errors?.password?.message}
        />
        <PasswordFormField
          error={errors.confirmPassword}
          label="Confirm Password"
          name="confirmPassword"
          control={control}
          hint={errors?.confirmPassword?.message}
          required
        />

        {error && (
          <Label as="h4" color="red" data-testid="error">
            {error}
          </Label>
        )}

        <StyledButton loading={loading} fluid type="submit">
          Set Password
        </StyledButton>
      </Form>
    </Box>
  );
}
LoginBox.displayName = 'LoginBox';

export function RequestPasswordPage() {
  const [logging, setLogging] = useState(false);
  const [success, setSuccess] = useState('');
  const [invalidToken, setInvalidToken] = useState('');
  const [searchQuery] = useSearchParams();
  const token = searchQuery.get('token');

  const navigate = useNavigate();

  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useForm({
    resolver: yupResolver(ResetPasswordSchema),
    shouldUnregister: true,
  });

  useEffect(() => {
    setInvalidToken(!token);
  }, [token]);

  const onSubmit = async data => {
    try {
      setLogging(true);
      await UserService.resetPassword({
        token,
        password: data.password,
      });
      ToastMessage.success(
        'Password has been reset successfully. Please login to continue',
      );
      setSuccess(true);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLogging(false);
    }
  };

  const backToLogin = () => {
    navigate('/login');
  };

  return (
    <PageContainer>
      <LoginContainer>
        <LogoContainer>
          <Logo src={Images.blackLogo} />
        </LogoContainer>
        {invalidToken ? (
          <Box>
            <div className="mb-3">
              <DangerText>Link is invalid, please contact administrator</DangerText>
            </div>
            <StyledButton onClick={backToLogin} fluid>
              Back to Login
            </StyledButton>
          </Box>
        ) : success ? (
          <Box>
            <div className="mb-3">
              <SuccessText>
                Password has been reset successfully. Please login to continue
              </SuccessText>
            </div>
            <StyledButton onClick={backToLogin} fluid>
              Back to Login
            </StyledButton>
          </Box>
        ) : (
          <LoginBox
            control={control}
            errors={errors}
            loading={logging}
            loginClicked={handleSubmit(onSubmit)}
          />
        )}
      </LoginContainer>
    </PageContainer>
  );
}
